import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Subject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { MwCoreToolbarOptions } from './core-toolbar.options';
import { MwCoreToolbarService } from './core-toolbar.service';

export const DEFAULT_CORE_TOOLBAR_OPTIONS: MwCoreToolbarOptions = {
  allowBack: false,
  showLogo: true,
  showActions: true,
  showToggler: true,
  showCompactLogo: false,
  showFullscreenButton: true,
};

@Component({
  selector: 'mw-core-toolbar',
  templateUrl: './core-toolbar.component.html',
  styleUrls: ['./core-toolbar.component.scss'],
})
export class MwCoreToolbarComponent implements OnInit, OnDestroy {
  showSearch = false;
  options: MwCoreToolbarOptions = { ...DEFAULT_CORE_TOOLBAR_OPTIONS };
  isFullScreen = false;

  @Input() clickableTitle = false;

  @Input() set title(value: string) {
    this.options.title = value;
  }

  get title(): string {
    return this.options.title || '';
  }

  @Input() set showLogo(value: boolean) {
    this.options.showLogo = value;
  }

  @Input() set logo(value: string | null) {
    this.localLogo = value ?? this.defaultLogo;
  }

  get logo(): string {
    return this.options.showLogo
      ? this.localLogo !== this.defaultLogo
        ? this.localLogo
        : this.defaultLogo
      : null;
  }

  @Input() set showActions(value: boolean) {
    this.options.showActions = value;
  }

  get showActions(): boolean {
    return this.options.showActions === true;
  }

  @Input() set showFullscreenButton(value: boolean) {
    this.options.showFullscreenButton = value;
  }

  get showFullscreenButton(): boolean {
    const showFullscreenButton = this.options.showFullscreenButton;

    return showFullscreenButton === undefined ? true : showFullscreenButton;
  }

  @Input() set showCompactLogo(value: boolean) {
    this.options.showCompactLogo = value;
  }

  get showCompactLogo(): boolean {
    const showCompactLogo = this.options.showCompactLogo;
    return showCompactLogo === undefined ? false : showCompactLogo;
  }

  @Input() set allowBack(value: boolean) {
    this.options.allowBack = value;
  }
  get allowBack(): boolean {
    return this.options.allowBack === true;
  }

  @Input() set showToggler(value: boolean) {
    this.options.showToggler = value;
  }

  get showToggler(): boolean {
    return this.options.showToggler === true && this.allowBack === false;
  }

  @Output() titleClick = new EventEmitter();
  @Output() toggle = new EventEmitter();

  get showLogoLoadingEffect(): boolean {
    return this.localLogo !== this.defaultLogo;
  }

  defaultLogo = 'assets/img/logo.svg';

  private localLogo = 'assets/img/logo.svg';
  private elem?: any;
  private unsubscribeAll$ = new Subject<void>();

  constructor(
    private service: MwCoreToolbarService,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: any
  ) {}

  ngOnInit(): void {
    this.subscribeToNavigationEnd();
    this.subscribeToOptionsChange();
    this.subscribeToWindowsResize();

    this.elem = document.documentElement;
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  onTitleClick(): void {
    if (!this.clickableTitle) {
      return;
    }
    this.titleClick.emit();
  }

  onBackClick(): void {
    this.service.emitBackClickEvent();
  }

  openFullScreen(): void {
    if (!this.elem) {
      throw new Error('elem is not defined');
    }

    const requestFullscreen =
      this.elem.requestFullscreen ||
      this.elem.mozRequestFullScreen ||
      this.elem.webkitRequestFullscreen ||
      this.elem.msRequestFullscreen;

    if (requestFullscreen) {
      requestFullscreen.call(this.elem);
    }
  }

  closeFullScreen(): void {
    const exitFullScreen =
      this.document.exitFullscreen ||
      this.document.mozCancelFullScreen ||
      this.document.webkitExitFullscreen ||
      this.document.msExitFullscreen;

    if (exitFullScreen) {
      exitFullScreen.call(this.document);
    }
  }

  onLogoError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    if (!imgElement.src?.endsWith(this.defaultLogo)) {
      imgElement.src = this.defaultLogo;
    }
  }

  private subscribeToWindowsResize(): void {
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe(() => {
        const isFullScreenNow = 1 >= outerHeight - innerHeight;
        if (
          (isFullScreenNow && this.document.fullscreenElement) ||
          !isFullScreenNow
        ) {
          this.isFullScreen = isFullScreenNow;
        }

        this.changeDetectorRef.markForCheck();
      });
  }

  private subscribeToOptionsChange(): void {
    this.service.options$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((options) => {
        this.options = {
          ...this.options,
          ...options,
        };
        this.changeDetectorRef.detectChanges();
      });
  }

  private subscribeToNavigationEnd(): void {
    this.router.events
      .pipe(
        takeUntil(this.unsubscribeAll$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.options = DEFAULT_CORE_TOOLBAR_OPTIONS;
        this.changeDetectorRef.markForCheck();
      });
  }
}
